import {
    CaretRightOutlined,
    CheckCircleFilled,
    DownOutlined,
    PauseCircleFilled,
    PauseOutlined,
    ReloadOutlined,
    StopFilled,
    ThunderboltOutlined,
} from '@ant-design/icons';
import type {MenuProps, TableProps} from 'antd';
import {
    Alert,
    Button,
    Card,
    Col,
    Dropdown,
    Flex,
    Popconfirm,
    Progress,
    Row,
    Select,
    Statistic,
    Table,
    Tag,
    theme,
    Tooltip,
    Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, {useState} from 'react';
import {Link} from 'react-router-dom';

import {ApiQuery} from '../../../gen/api';
import {AdminWorkerJobsStateEnum} from '../../../gen/api/apis/AdminWorkerApi';
import {AdminWorkerJob, AdminWorkerJobState, AdminWorkerKindStats, AdminWorkerRunJob} from '../../../gen/api/models';
import {useNotification} from '../../../NotificationContext';
import {useScreenInfo} from '../../../ResponsiveGate';
import {formatAgo} from '../dashboard/helpers/slowSelling';

const {Text, Title} = Typography;

/** How often the page reloads the worker state. */
const REFRESH_MS = 10_000;
const PAGE_SIZE = 20;
const ALL = 'all';

/** Human names of the worker's job kinds (River job kinds). */
const KIND_INFO: Record<string, {label: string; hint: string}> = {
    refresh_market: {
        label: 'Market refresh',
        hint: 'Searches eBay for comparables of a product and records its market. Products with the same ASIN and condition share one search.',
    },
    recompute_product: {
        label: 'Recompute analytics',
        hint: 'Recomputes the listing analytics of a product (suggested price, floor, status).',
    },
    check_own_listing: {
        label: 'Own listing check',
        hint: 'Reads our eBay listing: live or gone, condition and price; copies a changed eBay price to the listing.',
    },
    schedule_market_refresh: {
        label: 'Market refresh scheduler',
        hint: 'Every hour: queues market refresh for 1/24 of the markets, oldest first.',
    },
    schedule_listing_checks: {
        label: 'Listing check scheduler',
        hint: 'Daily at 05:00 and on start: queues checks of live eBay listings not checked for a day.',
    },
    ebay_publish_listing: {
        label: 'Publish on eBay',
        hint: 'Uploads the product photos to eBay and publishes (or relists) the listing, or sends a live listing’s changes (Inventory API).',
    },
    ebay_import_orders: {
        label: 'Import eBay orders',
        hint: 'Every 15 minutes: turns paid eBay orders of listings published from the admin into orders (product sold) and handles their cancellations.',
    },
    ebay_end_listing: {
        label: 'End on eBay',
        hint: 'Ends a live eBay listing (withdraws its offer; it can be relisted).',
    },
    reindex_product: {
        label: 'Reindex product',
        hint: 'Writes a product to the search index after an eBay job changed its eBay listing row (published, relisted, ended, sold).',
    },
    recompute_all: {
        label: 'Recompute all',
        hint: 'Nightly at 03:00 and on start: queues analytics recompute for every product in sale.',
    },
};

const kindLabel = (kind: string) => KIND_INFO[kind]?.label ?? kind;

const STATE_COLOR: Record<AdminWorkerJobState, string> = {
    available: 'blue',
    scheduled: 'default',
    pending: 'default',
    running: 'processing',
    retryable: 'orange',
    completed: 'green',
    discarded: 'red',
    cancelled: 'red',
};

const empty = <Text type="secondary">—</Text>;

/** What "Run now" can start, in the order of the menu. */
const RUN_NOW: Array<{job: AdminWorkerRunJob; label: string; hint: string}> = [
    {
        job: AdminWorkerRunJob.WorkerRunJobMarketRefresh,
        label: 'Refresh market data',
        hint: 'Search eBay for every product whose market is older than 20 hours (one search per ASIN and condition).',
    },
    {
        job: AdminWorkerRunJob.WorkerRunJobListingChecks,
        label: 'Check own eBay listings',
        hint: 'Check every live eBay listing not checked in the last 20 hours and sync changed prices.',
    },
    {
        job: AdminWorkerRunJob.WorkerRunJobEbayOrders,
        label: 'Import eBay orders',
        hint: 'Import the eBay orders of listings published from the admin now instead of waiting up to 15 minutes.',
    },
    {
        job: AdminWorkerRunJob.WorkerRunJobRecomputeAll,
        label: 'Recompute analytics',
        hint: 'Recompute the price analytics of every product in sale from the data already collected (no eBay calls).',
    },
];

function formatTime(value?: string | Date | null): React.ReactNode {
    if (!value) {
        return empty;
    }
    const iso = typeof value === 'string' ? value : value.toISOString();
    return <Tooltip title={dayjs(iso).format('MM/DD/YYYY HH:mm:ss')}>{formatAgo(iso)}</Tooltip>;
}

/** "in 12m" / "now" for a time in the future, otherwise "3h ago". */
function formatUntil(value?: string | Date | null): React.ReactNode {
    if (!value) {
        return empty;
    }
    const iso = typeof value === 'string' ? value : value.toISOString();
    const minutes = Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
    if (minutes <= 0) {
        return formatTime(iso);
    }
    const text = minutes < 120 ? `in ${minutes}m` : `in ${Math.round(minutes / 60)}h`;
    return <Tooltip title={dayjs(iso).format('MM/DD/YYYY HH:mm:ss')}>{text}</Tooltip>;
}

/** Job arguments, with product ids linked to the product page. */
const JobArgs: React.FC<{args: object}> = ({args}) => {
    const entries = Object.entries(args as Record<string, unknown>);
    if (entries.length === 0) {
        return empty;
    }
    return (
        <Flex
            vertical
            gap={0}
        >
            {entries.map(([key, value]) => (
                <Text
                    key={key}
                    style={{fontSize: 12}}
                >
                    <Text type="secondary">{key}: </Text>
                    {key === 'product_id' && typeof value === 'string' ? (
                        <Link to={`/products/${value}`}>{value.slice(0, 8)}…</Link>
                    ) : (
                        String(value)
                    )}
                </Text>
            ))}
        </Flex>
    );
};

const kindColumns: TableProps<AdminWorkerKindStats>['columns'] = [
    {
        title: 'Job',
        dataIndex: 'kind',
        key: 'kind',
        render: (kind: string) => (
            <Tooltip title={KIND_INFO[kind]?.hint}>
                <Text strong>{kindLabel(kind)}</Text>{' '}
                <Text
                    type="secondary"
                    style={{fontSize: 12}}
                >
                    {kind}
                </Text>
            </Tooltip>
        ),
    },
    {title: 'Queued', dataIndex: 'queued', key: 'queued', width: 90},
    {
        title: 'Running',
        dataIndex: 'running',
        key: 'running',
        width: 90,
        render: (value: number) => (value > 0 ? <Tag color="processing">{value}</Tag> : value),
    },
    {title: 'Done 24h', dataIndex: 'completed24h', key: 'completed24h', width: 100},
    {
        title: 'Failed 24h',
        dataIndex: 'failed24h',
        key: 'failed24h',
        width: 100,
        render: (value: number) => (value > 0 ? <Text type="danger">{value}</Text> : value),
    },
    {
        title: 'Last done',
        dataIndex: 'lastCompletedAt',
        key: 'lastCompletedAt',
        width: 120,
        render: (value?: Date) => formatTime(value),
    },
    {
        title: 'Last failed',
        dataIndex: 'lastFailedAt',
        key: 'lastFailedAt',
        width: 120,
        render: (value?: Date) => formatTime(value),
    },
    {
        title: 'Next run',
        dataIndex: 'nextScheduledAt',
        key: 'nextScheduledAt',
        width: 110,
        render: (value?: Date) => formatUntil(value),
    },
];

const jobColumns: TableProps<AdminWorkerJob>['columns'] = [
    {title: 'ID', dataIndex: 'id', key: 'id', width: 80},
    {
        title: 'Job',
        dataIndex: 'kind',
        key: 'kind',
        width: 190,
        render: (kind: string) => <Tooltip title={kind}>{kindLabel(kind)}</Tooltip>,
    },
    {
        title: 'Product',
        dataIndex: 'product',
        key: 'product',
        width: 260,
        render: (product?: AdminWorkerJob['product']) =>
            product ? (
                <Link
                    to={`/products/${product.id}`}
                    style={{display: 'block', maxWidth: 260}}
                >
                    <Text
                        ellipsis={{tooltip: product.name}}
                        style={{color: 'inherit', maxWidth: 260}}
                    >
                        {product.name}
                    </Text>
                    <Text
                        type="secondary"
                        style={{fontSize: 12, display: 'block'}}
                    >
                        #{product.listingNumber}
                    </Text>
                </Link>
            ) : (
                empty
            ),
    },
    {
        title: 'State',
        dataIndex: 'state',
        key: 'state',
        width: 110,
        render: (state: AdminWorkerJobState) => <Tag color={STATE_COLOR[state]}>{state}</Tag>,
    },
    {
        title: 'Args',
        dataIndex: 'args',
        key: 'args',
        width: 230,
        render: (args: object) => <JobArgs args={args} />,
    },
    {
        title: 'Attempt',
        key: 'attempt',
        width: 90,
        render: (_, job) => `${job.attempt} / ${job.maxAttempts}`,
    },
    {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 110,
        render: (value: Date) => formatTime(value),
    },
    {
        title: 'Finished',
        key: 'finishedAt',
        width: 130,
        render: (_, job) => {
            if (!job.finalizedAt) {
                return job.state === 'running' ? <Text type="secondary">running…</Text> : formatUntil(job.scheduledAt);
            }
            const started = job.attemptedAt ? new Date(job.attemptedAt).getTime() : undefined;
            const took = started !== undefined ? new Date(job.finalizedAt).getTime() - started : undefined;
            return (
                <>
                    {formatTime(job.finalizedAt)}
                    {took !== undefined ? (
                        <Text
                            type="secondary"
                            style={{fontSize: 12}}
                        >
                            {' '}
                            · {took < 1000 ? `${took}ms` : `${(took / 1000).toFixed(1)}s`}
                        </Text>
                    ) : null}
                </>
            );
        },
    },
    {
        title: 'Last error',
        dataIndex: 'lastError',
        key: 'lastError',
        render: (value?: string) =>
            value ? (
                <Text
                    type="danger"
                    style={{fontSize: 12}}
                    ellipsis={{tooltip: value}}
                >
                    {value}
                </Text>
            ) : (
                empty
            ),
    },
];

export const WorkerPage: React.FC = () => {
    const {token} = theme.useToken();
    const {screenMap} = useScreenInfo();
    const [state, setState] = useState<AdminWorkerJobsStateEnum | typeof ALL>(ALL);
    const [kind, setKind] = useState<string>(ALL);
    const [page, setPage] = useState(1);

    const notify = useNotification();
    const statusQuery = ApiQuery.useAdminWorkerStatusQuery({refetchInterval: REFRESH_MS});
    const jobsQuery = ApiQuery.useAdminWorkerJobsQuery(
        {
            state: state === ALL ? undefined : state,
            kind: kind === ALL ? undefined : kind,
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
        },
        {refetchInterval: REFRESH_MS, keepPreviousData: true}
    );

    const status = statusQuery.data;
    const kinds = status?.kinds ?? [];
    const coverage = status?.coverage;
    const queued = kinds.reduce((sum, k) => sum + k.queued + k.running, 0);
    const failed = kinds.reduce((sum, k) => sum + k.failed24h, 0);
    const kindOptions = Array.from(new Set([...Object.keys(KIND_INFO), ...kinds.map((k) => k.kind)]));

    const share = (part?: number, total?: number) => (total ? Math.round(((part ?? 0) / total) * 100) : 0);

    const refetchAll = () => {
        statusQuery.refetch();
        jobsQuery.refetch();
    };
    const onQueueError = (action: string) => (errors: Array<{message?: string}>) =>
        notify.openNotificationWithIcon('error', `Failed to ${action} the worker`, errors?.[0]?.message);
    const pauseMutation = ApiQuery.useAdminPauseWorkerMutation({
        onSuccess: () => {
            notify.openNotificationWithIcon(
                'info',
                'Worker paused',
                'Running jobs finish; new ones wait until Resume.'
            );
            refetchAll();
        },
        onError: onQueueError('pause'),
    });
    const resumeMutation = ApiQuery.useAdminResumeWorkerMutation({
        onSuccess: () => {
            notify.openNotificationWithIcon('success', 'Worker resumed');
            refetchAll();
        },
        onError: onQueueError('resume'),
    });
    const runMutation = ApiQuery.useAdminRunWorkerJobMutation({
        onSuccess: (data, variables) => {
            const label = RUN_NOW.find((r) => r.job === variables.params.job)?.label ?? variables.params.job;
            notify.openNotificationWithIcon(
                data.queued ? 'success' : 'info',
                data.queued ? `${label}: queued` : `${label}: already queued or running`,
                status?.paused ? 'The worker is paused: it starts after Resume.' : undefined
            );
            refetchAll();
        },
        onError: onQueueError('queue a job for'),
    });

    const runNowItems: MenuProps['items'] = RUN_NOW.map((r) => ({
        key: r.job,
        label: (
            <Tooltip
                title={r.hint}
                placement="left"
            >
                {r.label}
            </Tooltip>
        ),
    }));

    const statusLabel = !status?.running ? 'Stopped' : status.paused ? 'Paused' : 'Running';
    const statusColor = !status?.running ? token.colorError : status.paused ? token.colorWarning : token.colorSuccess;
    const statusIcon = !status?.running ? (
        <StopFilled />
    ) : status.paused ? (
        <PauseCircleFilled />
    ) : (
        <CheckCircleFilled />
    );

    return (
        <Flex
            vertical
            gap={16}
        >
            <Flex
                justify="space-between"
                align="center"
            >
                <Title
                    level={screenMap.lg ? 3 : 5}
                    style={{margin: 0}}
                >
                    Worker
                </Title>
                <Flex
                    gap={8}
                    wrap
                    justify="end"
                >
                    {status?.paused ? (
                        <Button
                            type="primary"
                            icon={<CaretRightOutlined />}
                            loading={resumeMutation.isLoading}
                            onClick={() => resumeMutation.mutate(undefined)}
                        >
                            Resume
                        </Button>
                    ) : (
                        <Popconfirm
                            title="Pause the worker?"
                            description="It stops taking new jobs; running jobs finish. Scheduled jobs wait until Resume."
                            okText="Pause"
                            onConfirm={() => pauseMutation.mutate(undefined)}
                        >
                            <Button
                                icon={<PauseOutlined />}
                                loading={pauseMutation.isLoading}
                                disabled={!status}
                            >
                                Pause
                            </Button>
                        </Popconfirm>
                    )}
                    <Dropdown
                        menu={{
                            items: runNowItems,
                            onClick: ({key}) => runMutation.mutate({params: {job: key as AdminWorkerRunJob}}),
                        }}
                    >
                        <Button
                            icon={<ThunderboltOutlined />}
                            loading={runMutation.isLoading}
                        >
                            Run now <DownOutlined />
                        </Button>
                    </Dropdown>
                    <Tooltip title={`Refreshes every ${REFRESH_MS / 1000}s`}>
                        <Button
                            icon={<ReloadOutlined spin={statusQuery.isFetching || jobsQuery.isFetching} />}
                            onClick={refetchAll}
                        >
                            Refresh
                        </Button>
                    </Tooltip>
                </Flex>
            </Flex>

            {statusQuery.isError ? (
                <Alert
                    type="error"
                    showIcon
                    message="Failed to load the worker status"
                />
            ) : status && !status.running ? (
                <Alert
                    type="warning"
                    showIcon
                    message="The worker is not running"
                    description={
                        <>
                            Markets are not refreshed and listings are not checked until it is started:{' '}
                            <Text code>cd server && go run ./cmd worker</Text>
                            {status.leaderExpiresAt ? (
                                <> Last seen {formatAgo(String(status.leaderExpiresAt))}.</>
                            ) : null}
                        </>
                    }
                />
            ) : status?.paused ? (
                <Alert
                    type="info"
                    showIcon
                    message="The worker is paused"
                    description={
                        <>
                            Paused {formatAgo(String(status.pausedAt))}. It takes no new jobs; scheduled and queued jobs
                            run after Resume.
                        </>
                    }
                />
            ) : null}

            <Row gutter={[16, 16]}>
                <Col
                    xs={24}
                    md={12}
                    xl={6}
                >
                    <Card
                        size="small"
                        loading={statusQuery.isLoading}
                    >
                        <Statistic
                            title="Status"
                            value={statusLabel}
                            valueStyle={{color: statusColor}}
                            prefix={statusIcon}
                        />
                        <Text
                            type="secondary"
                            style={{fontSize: 12}}
                        >
                            {status?.running && status.leaderSince ? (
                                <>Started {formatAgo(String(status.leaderSince))}</>
                            ) : (
                                'No worker process holds the queue'
                            )}
                        </Text>
                    </Card>
                </Col>
                <Col
                    xs={24}
                    md={12}
                    xl={6}
                >
                    <Card
                        size="small"
                        loading={statusQuery.isLoading}
                    >
                        <Statistic
                            title="Queued and running"
                            value={queued}
                        />
                        <Text
                            type={failed > 0 ? 'danger' : 'secondary'}
                            style={{fontSize: 12}}
                        >
                            {failed} failed in 24h
                        </Text>
                    </Card>
                </Col>
                <Col
                    xs={24}
                    md={12}
                    xl={6}
                >
                    <Card
                        size="small"
                        loading={statusQuery.isLoading}
                    >
                        <Tooltip title="Products in sale with an eBay market captured in the last 24 hours (refreshed about once a day)">
                            <Text type="secondary">Fresh market data</Text>
                        </Tooltip>
                        <Progress
                            percent={share(coverage?.productsWithFreshMarket, coverage?.productsInSale)}
                            size="small"
                        />
                        <Text style={{fontSize: 12}}>
                            {coverage?.productsWithFreshMarket ?? 0} of {coverage?.productsInSale ?? 0} products
                        </Text>
                    </Card>
                </Col>
                <Col
                    xs={24}
                    md={12}
                    xl={6}
                >
                    <Card
                        size="small"
                        loading={statusQuery.isLoading}
                    >
                        <Tooltip title="Live eBay listings of products in sale checked on eBay in the last 24 hours">
                            <Text type="secondary">eBay listings checked</Text>
                        </Tooltip>
                        <Progress
                            percent={share(coverage?.ebayListingsChecked, coverage?.ebayListings)}
                            size="small"
                        />
                        <Text style={{fontSize: 12}}>
                            {coverage?.ebayListingsChecked ?? 0} of {coverage?.ebayListings ?? 0} listings
                        </Text>
                    </Card>
                </Col>
            </Row>

            <Card
                size="small"
                title="Job types"
            >
                <Table<AdminWorkerKindStats>
                    size="small"
                    rowKey="kind"
                    loading={statusQuery.isLoading}
                    columns={kindColumns}
                    dataSource={kinds}
                    pagination={false}
                    scroll={{x: 'max-content'}}
                />
            </Card>

            <Card
                size="small"
                title="Jobs"
                extra={
                    <Flex
                        gap={8}
                        wrap
                    >
                        <Select
                            size="small"
                            style={{width: 140}}
                            value={state}
                            onChange={(value) => {
                                setState(value);
                                setPage(1);
                            }}
                            options={[
                                {label: 'All states', value: ALL},
                                ...Object.values(AdminWorkerJobsStateEnum).map((value) => ({label: value, value})),
                            ]}
                        />
                        <Select
                            size="small"
                            style={{width: 200}}
                            value={kind}
                            onChange={(value) => {
                                setKind(value);
                                setPage(1);
                            }}
                            options={[
                                {label: 'All jobs', value: ALL},
                                ...kindOptions.map((value) => ({label: kindLabel(value), value})),
                            ]}
                        />
                    </Flex>
                }
            >
                <Table<AdminWorkerJob>
                    size="small"
                    rowKey="id"
                    loading={jobsQuery.isLoading}
                    columns={jobColumns}
                    dataSource={jobsQuery.data?.items ?? []}
                    scroll={{x: 'max-content'}}
                    pagination={{
                        current: page,
                        pageSize: PAGE_SIZE,
                        total: jobsQuery.data?.total ?? 0,
                        showSizeChanger: false,
                        showTotal: (total) => `${total} jobs`,
                        onChange: setPage,
                    }}
                />
            </Card>
        </Flex>
    );
};
